import { useTranslation } from "react-i18next";

import { ActionButton, Card, typography } from "@dsaui/components";
import { cn } from "@dsaui/utils";
import { Tooltip } from "@dsaui/components/Tooltip";
import { ArrowPath, CheckCircle, ExclamationCircle } from "@dsaui/icons";

type MembershipStatusIconName = "check" | "exclamation";

const membershipStatusIcons = {
  check: CheckCircle,
  exclamation: ExclamationCircle,
};

export interface WelcomeCardProps {
  chapter: string;
  email: string;
  loading?: boolean;
  membershipStatus: string;
  membershipStatusIcon?: MembershipStatusIconName | null;
  membershipStatusIsGoodStanding: boolean;
  name: string;
  proofActionHref: string | null;
  proofActionLabel: string;
  showChapterTooltip?: boolean;
}

const WelcomeCardSkeleton = () => {
  const { t } = useTranslation();

  return (
    <Card
      aria-busy="true"
      aria-label={t("dashboard.loading.welcomeCard")}
      className="animate-pulse"
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex flex-col gap-3">
          <div className="h-5 w-32 rounded-md bg-gray-300" />
          <div className="h-4 w-40 rounded-md bg-gray-400" />
        </div>
        <div className="space-y-3">
          <div className="h-5 w-52 rounded-md bg-gray-300" />
          <div className="h-4 w-28 rounded-md bg-gray-300" />
          <div className="h-11 w-full rounded-md bg-gray-200 lg:w-56" />
        </div>
      </div>
    </Card>
  );
};

export const WelcomeCard = ({
  chapter,
  email,
  loading = false,
  membershipStatus,
  membershipStatusIcon = null,
  membershipStatusIsGoodStanding,
  name,
  proofActionHref,
  proofActionLabel,
  showChapterTooltip = true,
}: WelcomeCardProps) => {
  const { t } = useTranslation();
  const nameClassName = cn(
    typography.profileTitleMobile.className,
    "lg:text-2xl lg:leading-8",
  );
  const secondaryTextClassName = "text-base leading-6 text-gray-600";
  const StatusIcon = membershipStatusIcon
    ? membershipStatusIcons[membershipStatusIcon]
    : null;

  if (loading) return <WelcomeCardSkeleton />;

  return (
    <Card>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex flex-col gap-3 lg:items-start">
          <h1 className={nameClassName}>{name}</h1>
          <p className={secondaryTextClassName}>{email}</p>
        </div>

        <div className="flex flex-col gap-3 lg:items-start">
          <div className="flex items-center gap-1">
            <p className={secondaryTextClassName}>{membershipStatus}</p>
            {StatusIcon && (
              <StatusIcon
                className={
                  membershipStatusIsGoodStanding
                    ? "size-5 text-green-600"
                    : "size-5 text-yellow-700"
                }
              />
            )}
          </div>

          <div className="flex items-center gap-1">
            <p className={cn(secondaryTextClassName)}>
              {t("dashboard.welcome.chapter", { chapter })}
            </p>
            {showChapterTooltip && (
              <Tooltip
                id="dashboard-chapter-tooltip"
                message={t("dashboard.welcome.chapterTooltip")}
              />
            )}
          </div>

          {proofActionHref && (
            <div className="w-full lg:w-auto">
              <ActionButton href={proofActionHref} rel="noreferrer" target="_blank">
                <span className="inline-flex items-center justify-center gap-2">
                  {proofActionLabel}
                  {!membershipStatusIsGoodStanding && <ArrowPath className="size-5" />}
                </span>
              </ActionButton>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default WelcomeCard;
